<script setup lang="ts">
import type { Entitlement } from '#layers/access';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePolicyEntitlements } from '~/composables/policy';
import { usePermissionsStore } from '~/stores/permissions';

const modelValue = defineModel<Entitlement[]>({ required: true });

const { enabledActionsForResource } = usePolicyEntitlements({ entitlements: modelValue });
const { resources } = storeToRefs(usePermissionsStore());
const { t } = useI18n();

const items = computed(() => resources.value.map((resource) => ({ label: t(`policy.entitlements.${resource.name}.title`), ...resource })));
</script>

<template>
	<div class="flex flex-col gap-4">
		<DNotice color="neutral" :description="$t('policy.entitlements.explainer')" />
		<DAccordion
			:items
			type="multiple"
			class="rounded-sm border"
			:ui="{ content: 'divide-y border-t' }"
		>
			<template #content="{ item: resource }">
				<div
					v-for="action in resource.actions"
					:key="action.name"
					class="flex h-12 py-2 pr-6 pl-4"
				>
					<PolicyEntitlementResourceAction v-model="modelValue" :resource :action />
				</div>
			</template>
			<template #trailing="{ item: resource }">
				<DBadge
					rounded
					variant="soft"
					:color="enabledActionsForResource(resource).length > 0 ? 'primary' : 'neutral'"
					:label="`${enabledActionsForResource(resource).length}/${resource.actions.length}`"
				/>
			</template>
		</DAccordion>
	</div>
</template>
